import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
    Building2,
    Clock,
    Database,
    Globe,
    Plus,
    Save,
    Settings,
    Shield,
    Tag,
    Palette,
    Trash2,
    Upload,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeColorPicker } from '@/components/admin/ThemeColorPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import httpClient, { ApiResponse } from '../../api/httpClient';
import { API_ENDPOINTS } from '@/api/endpoints';
import {
    useDemoDataStatus,
    useDeleteDemoData,
    useCreateDemoData,
} from '@/api/demo-data/use-demo-data';
import { usePermissions } from '@/hooks/usePermissions';
import { useConfirm } from '@/hooks/useConfirm';
import { LeadStatusManager } from '@/components/lead-statuses/LeadStatusManager';
import { CustomAttributeDefinitionsManager } from '@/components/custom-attributes/CustomAttributeDefinitionsManager';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { PageContainer, PageHeader } from '@/components/shared/layout';

interface SystemConfiguration {
    system_name: string;
    logo_url?: string;
    system_favicon_url?: string;
    trader_favicon_url?: string;
    trader_app_icon_url?: string;
    support_email: string;
    support_phone?: string;
    support_address?: string;
    trading_enabled: boolean;
    registration_enabled: boolean;
    maintenance_mode: boolean;
    maintenance_message?: string;
    default_currency: string;
    timezone: string;
    date_format: string;
    time_format: string;
    max_login_attempts: number;
    session_timeout_minutes: number;
    theme_primary_color?: string | null;
    theme_accent_color?: string | null;
    theme_radius?: number | null;
    theme_default_mode?: 'light' | 'dark' | 'system' | null;
}

const SystemConfigurationPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const confirm = useConfirm();
    const canDeleteDemoData =
        hasPermission(ModulePermission.SYSTEM_SETTINGS, PermissionAction.EDIT) || false;
    const [config, setConfig] = useState<SystemConfiguration | null>(null);
    const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
    const [selectedFavicon, setSelectedFavicon] = useState<File | null>(null);
    const [selectedTraderFavicon, setSelectedTraderFavicon] = useState<File | null>(null);
    const [selectedTraderAppIcon, setSelectedTraderAppIcon] = useState<File | null>(null);
    const [activeTab, setActiveTab] = useState('general');

    // Demo data management
    const { data: demoDataStatus } = useDemoDataStatus();
    const deleteDemoDataMutation = useDeleteDemoData();
    const createDemoDataMutation = useCreateDemoData();

    const availableCurrencies = [
        { symbol: 'USD', name: 'US Dollar' },
        { symbol: 'EUR', name: 'Euro' },
    ];

    // Fetch system configuration
    const { isLoading: isConfigLoading } = useQuery<ApiResponse<SystemConfiguration>>({
        queryKey: ['system-configuration'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<SystemConfiguration>>(
                API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION
            );
            setConfig(data.data || data);
            return data;
        },
    });

    // Update configuration mutation
    const { mutate: updateConfig, isPending: isUpdating } = useMutation({
        mutationFn: async (configData: Partial<SystemConfiguration>) => {
            const { data } = await httpClient.put<ApiResponse<SystemConfiguration>>(
                API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION,
                configData
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            enqueueSnackbar('System configuration updated successfully', { variant: 'success' });
        },
        onError: () =>
            enqueueSnackbar('Failed to update system configuration', { variant: 'error' }),
    });

    // Upload logo mutation
    const { mutate: uploadLogo, isPending: isUploading } = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('logo', file);
            const { data } = await httpClient.post<ApiResponse<{ logo_url: string }>>(
                API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/logo',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            enqueueSnackbar('Logo uploaded successfully', { variant: 'success' });
            setSelectedLogo(null);
            // Logo preview removed
        },
        onError: () => enqueueSnackbar('Failed to upload logo', { variant: 'error' }),
    });

    // Delete logo mutation
    const { mutate: deleteLogo, isPending: isDeleting } = useMutation({
        mutationFn: async () => {
            await httpClient.delete(API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/logo');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            enqueueSnackbar('Logo deleted successfully', { variant: 'success' });
        },
        onError: () => enqueueSnackbar('Failed to delete logo', { variant: 'error' }),
    });

    // Upload favicon mutation
    const { mutate: uploadFavicon, isPending: isUploadingFavicon } = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('favicon', file);
            const { data } = await httpClient.post<ApiResponse<{ system_favicon_url: string }>>(
                API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/favicon',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            queryClient.invalidateQueries({ queryKey: ['system-configuration-public'] });
            enqueueSnackbar('Favicon uploaded successfully', { variant: 'success' });
            setSelectedFavicon(null);
        },
        onError: () => enqueueSnackbar('Failed to upload favicon', { variant: 'error' }),
    });

    // Delete favicon mutation
    const { mutate: deleteFavicon, isPending: isDeletingFavicon } = useMutation({
        mutationFn: async () => {
            await httpClient.delete(API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/favicon');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            queryClient.invalidateQueries({ queryKey: ['system-configuration-public'] });
            enqueueSnackbar('Favicon deleted successfully', { variant: 'success' });
        },
        onError: () => enqueueSnackbar('Failed to delete favicon', { variant: 'error' }),
    });

    // Upload trader favicon mutation
    const { mutate: uploadTraderFavicon, isPending: isUploadingTraderFavicon } = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('favicon', file);
            const { data } = await httpClient.post<ApiResponse<{ trader_favicon_url: string }>>(
                API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/trader-favicon',
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            queryClient.invalidateQueries({ queryKey: ['system-configuration-public'] });
            enqueueSnackbar('Trader favicon uploaded successfully', { variant: 'success' });
            setSelectedTraderFavicon(null);
        },
        onError: () => enqueueSnackbar('Failed to upload trader favicon', { variant: 'error' }),
    });

    // Delete trader favicon mutation
    const { mutate: deleteTraderFavicon, isPending: isDeletingTraderFavicon } = useMutation({
        mutationFn: async () => {
            await httpClient.delete(API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/trader-favicon');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            queryClient.invalidateQueries({ queryKey: ['system-configuration-public'] });
            enqueueSnackbar('Trader favicon deleted successfully', { variant: 'success' });
        },
        onError: () => enqueueSnackbar('Failed to delete trader favicon', { variant: 'error' }),
    });

    // Upload trader app icon mutation
    const { mutate: uploadTraderAppIcon, isPending: isUploadingTraderAppIcon } = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('icon', file);
            const { data } = await httpClient.post<ApiResponse<{ trader_app_icon_url: string }>>(
                API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/trader-app-icon',
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            queryClient.invalidateQueries({ queryKey: ['system-configuration-public'] });
            enqueueSnackbar('Trader app icon uploaded successfully', { variant: 'success' });
            setSelectedTraderAppIcon(null);
        },
        onError: () => enqueueSnackbar('Failed to upload trader app icon', { variant: 'error' }),
    });

    // Delete trader app icon mutation
    const { mutate: deleteTraderAppIcon, isPending: isDeletingTraderAppIcon } = useMutation({
        mutationFn: async () => {
            await httpClient.delete(API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/trader-app-icon');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            queryClient.invalidateQueries({ queryKey: ['system-configuration-public'] });
            enqueueSnackbar('Trader app icon deleted successfully', { variant: 'success' });
        },
        onError: () => enqueueSnackbar('Failed to delete trader app icon', { variant: 'error' }),
    });

    // Toggle maintenance mode mutation
    const { mutate: toggleMaintenance, isPending: isToggling } = useMutation({
        mutationFn: async (data: { maintenance_mode: boolean; maintenance_message?: string }) => {
            const { data: response } = await httpClient.post<ApiResponse<SystemConfiguration>>(
                API_ENDPOINTS.CRM.SYSTEM_CONFIGURATION + '/maintenance',
                data
            );
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-configuration'] });
            enqueueSnackbar('Maintenance mode updated successfully', { variant: 'success' });
        },
        onError: () => enqueueSnackbar('Failed to update maintenance mode', { variant: 'error' }),
    });

    // Handle logo file selection
    const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedLogo(file);
            const reader = new FileReader();
            reader.onload = () => {
                // Logo preview removed
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle logo upload
    const handleLogoUpload = () => {
        if (selectedLogo) {
            uploadLogo(selectedLogo);
        }
    };

    // Handle favicon file selection
    const handleFaviconSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFavicon(file);
        }
    };

    // Handle favicon upload
    const handleFaviconUpload = () => {
        if (selectedFavicon) {
            uploadFavicon(selectedFavicon);
        }
    };

    // Handle trader favicon file selection
    const handleTraderFaviconSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) setSelectedTraderFavicon(file);
    };

    // Handle trader favicon upload
    const handleTraderFaviconUpload = () => {
        if (selectedTraderFavicon) uploadTraderFavicon(selectedTraderFavicon);
    };

    // Handle trader app icon file selection
    const handleTraderAppIconSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedTraderAppIcon(file);
        }
    };

    // Handle trader app icon upload
    const handleTraderAppIconUpload = () => {
        if (selectedTraderAppIcon) uploadTraderAppIcon(selectedTraderAppIcon);
    };

    // Handle configuration update
    const handleConfigUpdate = (field: keyof SystemConfiguration, value: any) => {
        if (config) {
            setConfig({ ...config, [field]: value });
        }
    };

    // Handle save configuration
    const handleSaveConfig = () => {
        if (config) {
            updateConfig(config);
        }
    };

    // Handle maintenance mode toggle
    const handleMaintenanceToggle = (enabled: boolean) => {
        toggleMaintenance({
            maintenance_mode: enabled,
            maintenance_message:
                config?.maintenance_message ||
                'System is under maintenance. Please try again later.',
        });
    };

    if (isConfigLoading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="System Configuration"
                description="Manage system branding, access control, and settings"
                actions={
                    <Button onClick={handleSaveConfig} disabled={isUpdating}>
                        <Save className="h-4 w-4" />
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                }
            />

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid w-full grid-cols-6">
                    <TabsTrigger value="general" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        General
                    </TabsTrigger>
                    <TabsTrigger value="branding" className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Branding
                    </TabsTrigger>
                    <TabsTrigger value="access" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Access Control
                    </TabsTrigger>
                    <TabsTrigger value="crm" className="flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        Lead Statuses
                    </TabsTrigger>
                    <TabsTrigger value="custom-attributes" className="flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        Custom Attributes
                    </TabsTrigger>
                    <TabsTrigger value="system" className="flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        System
                    </TabsTrigger>
                </TabsList>

                {/* General Settings Tab */}
                <TabsContent value="general" className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Regional Settings */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Globe className="h-5 w-5" />
                                    Regional Settings
                                </CardTitle>
                                <CardDescription>
                                    Configure timezone, currency, and date formats
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="default_currency">Default Currency</Label>
                                    <Select
                                        value={config?.default_currency || 'USD'}
                                        onValueChange={value =>
                                            handleConfigUpdate('default_currency', value)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={'Select currency'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableCurrencies?.map(currency => (
                                                <SelectItem
                                                    key={currency.symbol}
                                                    value={currency.symbol}
                                                >
                                                    {currency.symbol} - {currency.name}
                                                </SelectItem>
                                            )) || (
                                                <SelectItem value="none" disabled>
                                                    {'No currencies available'}
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="timezone">Timezone</Label>
                                    <Select
                                        value={config?.timezone || 'UTC'}
                                        onValueChange={value =>
                                            handleConfigUpdate('timezone', value)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="UTC">
                                                UTC - Coordinated Universal Time
                                            </SelectItem>
                                            <SelectItem value="America/New_York">
                                                EST - Eastern Time
                                            </SelectItem>
                                            <SelectItem value="America/Chicago">
                                                CST - Central Time
                                            </SelectItem>
                                            <SelectItem value="America/Denver">
                                                MST - Mountain Time
                                            </SelectItem>
                                            <SelectItem value="America/Los_Angeles">
                                                PST - Pacific Time
                                            </SelectItem>
                                            <SelectItem value="Europe/London">
                                                GMT - Greenwich Mean Time
                                            </SelectItem>
                                            <SelectItem value="Europe/Paris">
                                                CET - Central European Time
                                            </SelectItem>
                                            <SelectItem value="Asia/Tokyo">
                                                JST - Japan Standard Time
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="date_format">Date Format</Label>
                                    <Select
                                        value={config?.date_format || 'Y-m-d'}
                                        onValueChange={value =>
                                            handleConfigUpdate('date_format', value)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Y-m-d">YYYY-MM-DD</SelectItem>
                                            <SelectItem value="d/m/Y">DD/MM/YYYY</SelectItem>
                                            <SelectItem value="m/d/Y">MM/DD/YYYY</SelectItem>
                                            <SelectItem value="d-m-Y">DD-MM-YYYY</SelectItem>
                                            <SelectItem value="m-d-Y">MM-DD-YYYY</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="time_format">Time Format</Label>
                                    <Select
                                        value={config?.time_format || 'H:i:s'}
                                        onValueChange={value =>
                                            handleConfigUpdate('time_format', value)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="H:i:s">
                                                24-hour (HH:MM:SS)
                                            </SelectItem>
                                            <SelectItem value="h:i:s A">
                                                12-hour (HH:MM:SS AM/PM)
                                            </SelectItem>
                                            <SelectItem value="H:i">24-hour (HH:MM)</SelectItem>
                                            <SelectItem value="h:i A">
                                                12-hour (HH:MM AM/PM)
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        {/* System Status */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Settings className="h-5 w-5" />
                                    System Status
                                </CardTitle>
                                <CardDescription>
                                    Current system status and configuration
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center p-4 border rounded-lg">
                                        <div className="text-2xl font-bold text-primary">
                                            {config?.trading_enabled ? 'Active' : 'Disabled'}
                                        </div>
                                        <div className="text-sm text-muted-foreground">Trading</div>
                                    </div>
                                    <div className="text-center p-4 border rounded-lg">
                                        <div className="text-2xl font-bold text-primary">
                                            {config?.registration_enabled ? 'Open' : 'Closed'}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            Registration
                                        </div>
                                    </div>
                                </div>

                                {config?.maintenance_mode && (
                                    <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                variant="secondary"
                                                className="bg-warning/10 text-warning"
                                            >
                                                Maintenance Mode
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-warning mt-2">
                                            {config.maintenance_message ||
                                                'System is under maintenance'}
                                        </p>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <h4 className="font-medium">Configuration Summary</h4>
                                    <div className="text-sm space-y-1 text-muted-foreground">
                                        <div>• System: {config?.system_name}</div>
                                        <div>• Currency: {config?.default_currency}</div>
                                        <div>• Timezone: {config?.timezone}</div>
                                        <div>
                                            • Max Login Attempts: {config?.max_login_attempts}
                                        </div>
                                        <div>
                                            • Session Timeout: {config?.session_timeout_minutes}{' '}
                                            minutes
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Branding Tab */}
                <TabsContent value="branding" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Palette className="h-5 w-5" />
                                Theme Colours
                            </CardTitle>
                            <CardDescription>
                                Set the brand colours used across the CRM and the trader portal.
                                Changes preview immediately and apply once saved.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ThemeColorPicker
                                value={{
                                    theme_primary_color: config?.theme_primary_color,
                                    theme_accent_color: config?.theme_accent_color,
                                    theme_radius: config?.theme_radius,
                                    theme_default_mode: config?.theme_default_mode,
                                }}
                                onChange={(field, next) => handleConfigUpdate(field, next)}
                                disabled={isUpdating}
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="h-5 w-5" />
                                System Branding
                            </CardTitle>
                            <CardDescription>
                                Configure system name, logo, and contact information
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="system_name">System Name</Label>
                                <Input
                                    id="system_name"
                                    value={config?.system_name || ''}
                                    onChange={e =>
                                        handleConfigUpdate('system_name', e.target.value)
                                    }
                                    placeholder="Enter system name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>System Logo</Label>
                                <div className="flex items-center gap-4">
                                    {config?.logo_url && (
                                        <img
                                            src={config.logo_url}
                                            alt="System Logo"
                                            className="h-16 w-16 object-contain border rounded"
                                        />
                                    )}
                                    <div className="flex-1">
                                        <Input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoSelect}
                                            className="cursor-pointer"
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Supported formats: JPEG, PNG, JPG, GIF, SVG (max 2MB)
                                        </p>
                                    </div>
                                </div>
                                {selectedLogo && (
                                    <div className="flex gap-2 mt-2">
                                        <Button
                                            size="sm"
                                            onClick={handleLogoUpload}
                                            disabled={isUploading}
                                        >
                                            <Upload className="h-3 w-3" />
                                            {isUploading ? 'Uploading...' : 'Upload'}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setSelectedLogo(null);
                                                // Logo preview removed
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )}
                                {config?.logo_url && (
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => deleteLogo()}
                                        disabled={isDeleting}
                                        className="mt-2"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                        {isDeleting ? 'Deleting...' : 'Delete Logo'}
                                    </Button>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label>System Favicon</Label>
                                <p className="text-sm text-muted-foreground mb-2">
                                    The favicon appears in browser tabs and bookmarks for the trader
                                    portal
                                </p>
                                <div className="flex items-center gap-4">
                                    {config?.system_favicon_url && (
                                        <div className="flex flex-col items-center gap-2">
                                            <img
                                                src={config.system_favicon_url}
                                                alt="System Favicon"
                                                className="h-16 w-16 object-contain border rounded"
                                            />
                                            <span className="text-xs text-muted-foreground">
                                                Preview
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <Input
                                            type="file"
                                            accept=".ico,.png,.svg"
                                            onChange={handleFaviconSelect}
                                            className="cursor-pointer"
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Supported formats: ICO, PNG, SVG (max 512KB)
                                        </p>
                                    </div>
                                </div>
                                {selectedFavicon && (
                                    <div className="flex gap-2 mt-2">
                                        <Button
                                            size="sm"
                                            onClick={handleFaviconUpload}
                                            disabled={isUploadingFavicon}
                                        >
                                            <Upload className="h-3 w-3" />
                                            {isUploadingFavicon ? 'Uploading...' : 'Upload'}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setSelectedFavicon(null);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )}
                                {config?.system_favicon_url && (
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => deleteFavicon()}
                                        disabled={isDeletingFavicon}
                                        className="mt-2"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                        {isDeletingFavicon ? 'Deleting...' : 'Delete Favicon'}
                                    </Button>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label>Trader Favicon</Label>
                                <p className="text-sm text-muted-foreground mb-2">
                                    Optional favicon for the trader portal. When set, it is used on
                                    trader pages instead of the system favicon above.
                                </p>
                                <div className="flex items-center gap-4">
                                    {config?.trader_favicon_url && (
                                        <div className="flex flex-col items-center gap-2">
                                            <img
                                                src={config.trader_favicon_url}
                                                alt="Trader Favicon"
                                                className="h-16 w-16 object-contain border rounded"
                                            />
                                            <span className="text-xs text-muted-foreground">
                                                Preview
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <Input
                                            type="file"
                                            accept=".ico,.png,.svg"
                                            onChange={handleTraderFaviconSelect}
                                            className="cursor-pointer"
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Supported formats: ICO, PNG, SVG (max 512KB)
                                        </p>
                                    </div>
                                </div>
                                {selectedTraderFavicon && (
                                    <div className="flex gap-2 mt-2">
                                        <Button
                                            size="sm"
                                            onClick={handleTraderFaviconUpload}
                                            disabled={isUploadingTraderFavicon}
                                        >
                                            <Upload className="h-3 w-3" />
                                            {isUploadingTraderFavicon ? 'Uploading...' : 'Upload'}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setSelectedTraderFavicon(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )}
                                {config?.trader_favicon_url && (
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => deleteTraderFavicon()}
                                        disabled={isDeletingTraderFavicon}
                                        className="mt-2"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                        {isDeletingTraderFavicon
                                            ? 'Deleting...'
                                            : 'Delete Trader Favicon'}
                                    </Button>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label>Trader App Icon</Label>
                                <p className="text-sm text-muted-foreground mb-2">
                                    PNG recommended, 512×512 square. Used for "Add to Home Screen"
                                    on mobile devices. Priority: App Icon &gt; Trader Favicon &gt;
                                    System Favicon.
                                </p>
                                <div className="flex items-center gap-4">
                                    {config?.trader_app_icon_url && (
                                        <div className="flex flex-col items-center gap-2">
                                            <img
                                                src={config.trader_app_icon_url}
                                                alt="Trader App Icon"
                                                className="h-16 w-16 object-contain border rounded"
                                            />
                                            <span className="text-xs text-muted-foreground">
                                                Preview
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <Input
                                            type="file"
                                            accept=".png"
                                            onChange={handleTraderAppIconSelect}
                                            className="cursor-pointer"
                                        />
                                        <p className="text-sm text-muted-foreground mt-1">
                                            PNG only, 512×512 recommended (max 2MB)
                                        </p>
                                    </div>
                                </div>
                                {selectedTraderAppIcon && (
                                    <div className="flex gap-2 mt-2">
                                        <Button
                                            size="sm"
                                            onClick={handleTraderAppIconUpload}
                                            disabled={isUploadingTraderAppIcon}
                                        >
                                            <Upload className="h-3 w-3" />
                                            {isUploadingTraderAppIcon ? 'Uploading...' : 'Upload'}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setSelectedTraderAppIcon(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )}
                                {config?.trader_app_icon_url && (
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => deleteTraderAppIcon()}
                                        disabled={isDeletingTraderAppIcon}
                                        className="mt-2"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                        {isDeletingTraderAppIcon
                                            ? 'Deleting...'
                                            : 'Delete Trader App Icon'}
                                    </Button>
                                )}
                            </div>

                            <Separator />

                            <div className="space-y-2">
                                <Label htmlFor="support_email">Support Email</Label>
                                <Input
                                    id="support_email"
                                    type="email"
                                    value={config?.support_email || ''}
                                    onChange={e =>
                                        handleConfigUpdate('support_email', e.target.value)
                                    }
                                    placeholder="support@example.com"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="support_phone">Support Phone</Label>
                                <Input
                                    id="support_phone"
                                    value={config?.support_phone || ''}
                                    onChange={e =>
                                        handleConfigUpdate('support_phone', e.target.value)
                                    }
                                    placeholder="+1-800-EXAMPLE"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="support_address">Support Address</Label>
                                <Textarea
                                    id="support_address"
                                    value={config?.support_address || ''}
                                    onChange={e =>
                                        handleConfigUpdate('support_address', e.target.value)
                                    }
                                    placeholder="Enter support address"
                                    rows={3}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Access Control Tab */}
                <TabsContent value="access" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Access Control
                            </CardTitle>
                            <CardDescription>
                                Control system access and user registration
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Trading Enabled</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Allow users to place trades
                                    </p>
                                </div>
                                <Switch
                                    checked={config?.trading_enabled || false}
                                    onCheckedChange={checked =>
                                        handleConfigUpdate('trading_enabled', checked)
                                    }
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Registration Enabled</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Allow new user registrations
                                    </p>
                                </div>
                                <Switch
                                    checked={config?.registration_enabled || false}
                                    onCheckedChange={checked =>
                                        handleConfigUpdate('registration_enabled', checked)
                                    }
                                />
                            </div>

                            <Separator />

                            <div className="space-y-2">
                                <Label>Maintenance Mode</Label>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <p className="text-sm font-medium">
                                            Enable Maintenance Mode
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            Temporarily disable system access
                                        </p>
                                    </div>
                                    <Switch
                                        checked={config?.maintenance_mode || false}
                                        onCheckedChange={handleMaintenanceToggle}
                                        disabled={isToggling}
                                    />
                                </div>
                                {config?.maintenance_mode && (
                                    <div className="mt-2">
                                        <Textarea
                                            value={config?.maintenance_message || ''}
                                            onChange={e =>
                                                handleConfigUpdate(
                                                    'maintenance_message',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Enter maintenance message"
                                            rows={2}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Max Login Attempts</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Maximum failed login attempts before lockout
                                    </p>
                                </div>
                                <Input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={config?.max_login_attempts || 5}
                                    onChange={e =>
                                        handleConfigUpdate(
                                            'max_login_attempts',
                                            parseInt(e.target.value)
                                        )
                                    }
                                    className="w-20"
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label>Session Timeout (minutes)</Label>
                                    <p className="text-sm text-muted-foreground">
                                        User session timeout duration
                                    </p>
                                </div>
                                <Input
                                    type="number"
                                    min="5"
                                    max="1440"
                                    value={config?.session_timeout_minutes || 120}
                                    onChange={e =>
                                        handleConfigUpdate(
                                            'session_timeout_minutes',
                                            parseInt(e.target.value)
                                        )
                                    }
                                    className="w-20"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Lead Status Management Tab */}
                <TabsContent value="crm" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Tag className="h-5 w-5" />
                                Lead Status Management
                            </CardTitle>
                            <CardDescription>
                                Manage lead statuses, colors, and system protection
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Alert variant="info">
                                <AlertTitle>Required contact attempts</AlertTitle>
                                <AlertDescription>
                                    Each lead can have a <strong>Required contact attempts</strong>{' '}
                                    value (set when creating or editing a lead, in Contact &amp;
                                    Follow-up). When this is greater than 0, the lead&apos;s status
                                    cannot be changed until contact attempts reach that number. Set
                                    it per lead in the lead form or on the lead detail page.
                                </AlertDescription>
                            </Alert>
                            <LeadStatusManager />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Custom Attributes Tab */}
                <TabsContent value="custom-attributes" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Tag className="h-5 w-5" />
                                Custom Attributes Management
                            </CardTitle>
                            <CardDescription>
                                Manage custom attribute definitions for Leads and Customers
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <CustomAttributeDefinitionsManager />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* System Tab */}
                <TabsContent value="system" className="space-y-6">
                    {/* Demo Data Management */}
                    {canDeleteDemoData && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Trash2 className="h-5 w-5" />
                                    Demo Data Management
                                </CardTitle>
                                <CardDescription>
                                    Manage demo data and system cleanup operations
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                variant={
                                                    demoDataStatus?.has_demo_data
                                                        ? 'default'
                                                        : 'secondary'
                                                }
                                            >
                                                {demoDataStatus?.has_demo_data
                                                    ? 'Demo Data Loaded'
                                                    : 'Demo Data Not Loaded'}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            Current demo data status and management options
                                        </p>
                                    </div>
                                    {demoDataStatus?.has_demo_data ? (
                                        <div className="flex gap-2">
                                            <Button
                                                variant="destructive"
                                                onClick={async () => {
                                                    const ok = await confirm({
                                                        title: 'Delete all demo data?',
                                                        description:
                                                            'This action cannot be undone and will remove all demo customers, transactions, leads, and other demo data while preserving essential system data.',
                                                        confirmLabel: 'Delete',
                                                        variant: 'destructive',
                                                    });
                                                    if (!ok) return;
                                                    try {
                                                        await deleteDemoDataMutation.mutateAsync();
                                                        enqueueSnackbar(
                                                            'Demo data deleted successfully',
                                                            {
                                                                variant: 'success',
                                                            }
                                                        );
                                                    } catch (error) {
                                                        enqueueSnackbar(
                                                            'Failed to delete demo data',
                                                            {
                                                                variant: 'error',
                                                            }
                                                        );
                                                    }
                                                }}
                                                disabled={deleteDemoDataMutation.isPending}
                                                className="flex items-center gap-2"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                                {deleteDemoDataMutation.isPending
                                                    ? 'Deleting...'
                                                    : 'Delete All Demo Data'}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={async () => {
                                                    const ok = await confirm({
                                                        title: 'Recreate demo data?',
                                                        description:
                                                            'This will delete existing demo data and create fresh sample data for testing purposes.',
                                                        confirmLabel: 'Recreate',
                                                        variant: 'destructive',
                                                    });
                                                    if (!ok) return;
                                                    try {
                                                        await deleteDemoDataMutation.mutateAsync();
                                                        await createDemoDataMutation.mutateAsync();
                                                        enqueueSnackbar(
                                                            'Demo data recreated successfully',
                                                            {
                                                                variant: 'success',
                                                            }
                                                        );
                                                    } catch (error) {
                                                        enqueueSnackbar(
                                                            'Failed to recreate demo data',
                                                            {
                                                                variant: 'error',
                                                            }
                                                        );
                                                    }
                                                }}
                                                disabled={
                                                    deleteDemoDataMutation.isPending ||
                                                    createDemoDataMutation.isPending
                                                }
                                                className="flex items-center gap-2"
                                            >
                                                <Clock className="h-4 w-4" />
                                                {deleteDemoDataMutation.isPending ||
                                                createDemoDataMutation.isPending
                                                    ? 'Recreating...'
                                                    : 'Recreate Demo Data'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            variant="default"
                                            onClick={async () => {
                                                const ok = await confirm({
                                                    title: 'Create demo data?',
                                                    description:
                                                        'This will create sample customers, trading accounts, and trading history for testing purposes.',
                                                    confirmLabel: 'Create',
                                                });
                                                if (!ok) return;
                                                try {
                                                    await createDemoDataMutation.mutateAsync();
                                                    enqueueSnackbar(
                                                        'Demo data created successfully',
                                                        {
                                                            variant: 'success',
                                                        }
                                                    );
                                                } catch (error) {
                                                    enqueueSnackbar('Failed to create demo data', {
                                                        variant: 'error',
                                                    });
                                                }
                                            }}
                                            disabled={createDemoDataMutation.isPending}
                                            className="flex items-center gap-2"
                                        >
                                            <Plus className="h-4 w-4" />
                                            {createDemoDataMutation.isPending
                                                ? 'Creating...'
                                                : 'Create Demo Data'}
                                        </Button>
                                    )}
                                </div>

                                {demoDataStatus?.statistics && (
                                    <div className="space-y-2">
                                        <h4 className="font-medium">Demo Data Statistics</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                            <div className="p-2 border rounded">
                                                <div className="font-medium">
                                                    {demoDataStatus.statistics.customers}
                                                </div>
                                                <div className="text-muted-foreground">
                                                    Customers
                                                </div>
                                            </div>
                                            <div className="p-2 border rounded">
                                                <div className="font-medium">
                                                    {demoDataStatus.statistics.transactions}
                                                </div>
                                                <div className="text-muted-foreground">
                                                    Transactions
                                                </div>
                                            </div>
                                            <div className="p-2 border rounded">
                                                <div className="font-medium">
                                                    {demoDataStatus.statistics.leads}
                                                </div>
                                                <div className="text-muted-foreground">Leads</div>
                                            </div>
                                            <div className="p-2 border rounded">
                                                <div className="font-medium">
                                                    {demoDataStatus.statistics.kyc_requests}
                                                </div>
                                                <div className="text-muted-foreground">
                                                    KYC Requests
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {demoDataStatus?.has_demo_data && (
                                    <Button
                                        variant="outline"
                                        onClick={async () => {
                                            const ok = await confirm({
                                                title: 'Create new demo data?',
                                                description:
                                                    'This will overwrite existing demo data.',
                                                confirmLabel: 'Recreate',
                                                variant: 'destructive',
                                            });
                                            if (!ok) return;
                                            try {
                                                await createDemoDataMutation.mutateAsync();
                                                enqueueSnackbar(
                                                    'Demo data recreated successfully',
                                                    {
                                                        variant: 'success',
                                                    }
                                                );
                                            } catch (error) {
                                                enqueueSnackbar('Failed to recreate demo data', {
                                                    variant: 'error',
                                                });
                                            }
                                        }}
                                        disabled={createDemoDataMutation.isPending}
                                        className="flex items-center gap-2"
                                    >
                                        <Clock className="h-4 w-4" />
                                        {createDemoDataMutation.isPending
                                            ? 'Recreating...'
                                            : 'Recreate Demo Data'}
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>
        </PageContainer>
    );
};

export default SystemConfigurationPage;
